import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";

// Görseller
import logo3 from "../assets/logo3.png";
import trFlag from "../assets/tr.png";
import gbFlag from "../assets/gb.png";

import "../css/Navbar.css";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const changeLang = () => {
    const newLang = i18n.language === "tr" ? "en" : "tr";
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Menü açıldığında sayfanın arkada kaymasını engelle (UX için kritik)
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [open]);

  const scrollToSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-[1000] transition-all duration-500 px-6 lg:px-12 
        ${scrolled ? "py-4 bg-[#060813] backdrop-blur-xl border-b border-white/5" : "py-8 bg-transparent"}`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* LOGO */}
          <div
            className="relative z-[1001] cursor-pointer transition-transform hover:scale-105"
            onClick={() => {
              navigate("/");
              setOpen(false);
            }}
          >
            <img
              src={logo3}
              alt="Logo"
              className="h-8 md:h-10 object-contain"
            />
          </div>

          {/* DESKTOP MENU */}
          <div className="hidden lg:flex items-center gap-1 bg-white/5 border border-white/10 p-1.5 rounded-full backdrop-blur-md">
            {[
              { name: t("homepage"), id: "home" },
              { name: t("about_us"), id: "home-about" },
              { name: t("references"), id: "home-references" },
              { name: t("contact"), id: "home-contact" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() =>
                  item.id === "home-references" && location.pathname !== "/"
                    ? navigate("/references")
                    : scrollToSection(item.id)
                }
                className="px-6 py-2 rounded-full text-sm font-bold text-gray-300 hover:text-white hover:bg-white/10 transition-all"
              >
                {item.name}
              </button>
            ))}
          </div>

          {/* ACTIONS & HAMBURGER */}
          <div className="flex items-center gap-4 relative z-[1001]">
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={changeLang}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:bg-white/5 transition-all"
              >
                <img
                  src={i18n.language === "tr" ? trFlag : gbFlag}
                  alt="flag"
                  className="w-5 h-5 rounded-full object-cover"
                />
                <span className="text-xs font-black text-white">
                  {i18n.language === "tr" ? "TR" : "EN"}
                </span>
              </button>
            </div>

            {/* HAMBURGER ICON */}
            <button
              className="lg:hidden text-white p-2 transition-transform active:scale-90"
              onClick={() => setOpen(!open)}
            >
              {open ? <X size={32} /> : <Menu size={32} />}
            </button>
          </div>
        </div>

        {/* --- MOBILE MENU OVERLAY (Fixed & Clean) --- */}
        <div
          className={`fixed inset-0 w-screen h-screen bg-[#050505] z-[1000] lg:hidden flex flex-col items-center justify-center gap-8 transition-all duration-500 ease-in-out
          ${open ? "translate-y-0 opacity-100 visible" : "-translate-y-full opacity-0 invisible"}`}
        >
          {/* Mobile Menü Linkleri */}
          <div className="flex flex-col items-center gap-6">
            {[
              { name: t("homepage"), id: "home" },
              { name: t("about_us"), id: "home-about" },
              { name: t("references"), id: "home-references" },
              { name: t("contact"), id: "home-contact" },
            ].map((item, index) => (
              <button
                key={item.id}
                onClick={() => {
                  if (
                    item.id === "home-references" &&
                    location.pathname !== "/"
                  ) {
                    navigate("/references");
                    setOpen(false);
                  } else {
                    scrollToSection(item.id);
                  }
                }}
                className={`text-3xl font-black text-white hover:text-[#2563eb] transition-all transform ${open ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {item.name}
              </button>
            ))}
          </div>

          {/* Mobil Dil Butonu */}
          <div
            className={`mt-8 transition-all duration-700 delay-500 ${open ? "opacity-100" : "opacity-0"}`}
          >
            <button
              onClick={changeLang}
              className="flex items-center gap-3 px-8 py-4 rounded-full border border-white/10 bg-white/5 active:bg-[#2563eb] transition-all"
            >
              <img
                src={i18n.language === "tr" ? trFlag : gbFlag}
                alt="flag"
                className="w-6 h-6 rounded-full"
              />
              <span className="font-bold text-xl text-white">
                {i18n.language === "tr" ? "English" : "Türkçe"}
              </span>
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}